import { ChevronDown, ChevronUp, Info } from "lucide-react";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { cn } from "src/common/utils";
import { ArtifactCard } from "src/components/ArtifactCard";
import { useReviewToolUseMutation } from "src/pages/AskPage/hooks/useReviewToolUseMutation";

import { LoadingSpinner } from "../../loading-spinner";
import { Markdown } from "../../Markdown";
import { ToolCallsSection_toolCalls$key } from "./__generated__/ToolCallsSection_toolCalls.graphql";
import { ChecklistToolCallResult } from "./ChecklistToolCallResult";
import { CardBox } from "./components";
import { ToolCallButton } from "./ToolCallButton";
import { ToolCallSheet } from "./ToolCallSheet";
import { V2ToolCallsList } from "./V2ToolCallsList";

interface PendingApprovalType {
  id: string;
  toolUseId: string;
  status: string;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
}

interface ToolCallsSectionProps {
  toolCalls: ToolCallsSection_toolCalls$key;
  metadata?: any;
  messageMetadata?: any;
  messageContent?: string;
  messageType?: any;
  isThinking?: boolean;
  thinking?: string;
  thinkingSummary?: string;
  disableAccordion?: boolean;
  defaultExpanded?: boolean;
  llmConversationId?: string;
  pendingToolUses?: ReadonlyArray<PendingApprovalType>;
  activeInteractionId?: string;
}



const fragment = graphql`
  fragment ToolCallsSection_toolCalls on GraphQLMessageToolCall
  @relay(plural: true) {
    text
    thinking
    ...ToolCallButton_toolCall
    ...ChecklistToolCallResult_toolCall
    ...ToolCallSheet_toolCall
    ...V2ToolButton_toolCall
    toolUse {
      id
      name
      displayName
      input {
        __typename
        ... on GenericToolInput {
          rawInput
        }
      }
    }
    toolResult {
      artifactId
      groupTags
    }
  }
`;

export const ToolCallsSection: React.FC<ToolCallsSectionProps> = ({
  toolCalls: toolCallsData,
  metadata,
  messageMetadata,
  messageContent,
  messageType,
  isThinking = false,
  thinking = "",
  thinkingSummary = "",
  disableAccordion = false,
  defaultExpanded,
  llmConversationId,
  pendingToolUses = [],
  activeInteractionId,
}) => {
  const toolCalls = useFragment(fragment, toolCallsData);
  const { reviewToolUse, isInFlight } = useReviewToolUseMutation();

  // Check if this is a v2 agent message
  // Prefer messageMetadata.agentVersion, fallback to metadata.agent_version for backwards compatibility
  const isV2Agent = messageMetadata?.agentVersion === "v2" || metadata?.agent_version === "v2";

  // State for the currently open sheet and active tool index
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [activeToolIndex, setActiveToolIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(
    defaultExpanded !== undefined ? defaultExpanded : isThinking
  );
  const [approvingToolId, setApprovingToolId] = useState<string | null>(null);
  const thinkingRef = useRef<HTMLDivElement>(null);
  const previousThinkingRef = useRef<string>(thinking);
  const prevIsThinkingRef = useRef(isThinking);

  // Log mount/unmount for debugging
  useEffect(() => {
    return () => {
    };
  }, []);

  // Clear approving state when tool disappears from pending list (data refetched)
  useEffect(() => {
    if (approvingToolId) {
      const stillPending = pendingToolUses.some(
        (toolUse) => toolUse.toolUseId === approvingToolId
      );
      if (!stillPending) {
        setApprovingToolId(null);
      }
    }
  }, [approvingToolId, pendingToolUses]);

  // Handle thinking text scrolling
  useEffect(() => {
    if (
      thinkingRef.current &&
      thinking &&
      thinking !== previousThinkingRef.current
    ) {
      setTimeout(() => {
        if (thinkingRef.current) {
          thinkingRef.current.scrollTop = thinkingRef.current.scrollHeight;
        }
      }, 50);
      previousThinkingRef.current = thinking;
    }
  }, [thinking]);

  // When isThinking changes, update the expanded state
  useEffect(() => {
    // If we are transitioning from thinking to not thinking, collapse the section
    if (prevIsThinkingRef.current && !isThinking) {
      setIsExpanded(false);
    } else {
      // Otherwise, expand if thinking is in progress
      setIsExpanded(isThinking);
    }
    prevIsThinkingRef.current = isThinking;
  }, [isThinking]);

  // Close sheet when accordion closes
  useEffect(() => {
    if (!isExpanded && isSheetOpen) {
      setIsSheetOpen(false);
    }
  }, [isExpanded, isSheetOpen]);

  // Helper to check if a tool is an MCP tool
  const isMcpTool = React.useCallback((toolCall: any) => {
    const toolName = toolCall?.toolUse?.name;
    return toolName && toolName.startsWith("mcp__");
  }, []);

  // Helper to check if a tool is a Filesystem tool
  const isFilesystemTool = React.useCallback((toolCall: any) => {
    const toolName = toolCall?.toolUse?.name;
    const filesystemTools = ["Read", "Grep", "Bash", "Write", "Edit", "Glob"];
    return toolName && filesystemTools.includes(toolName);
  }, []);

  // For V2, calculate the total count of MCP and Filesystem tools only
  // IMPORTANT: This useMemo must be called before any early returns to maintain hook order
  const totalNavigableTools = React.useMemo(() => {
    if (isV2Agent) {
      return toolCalls.filter(tc => isMcpTool(tc) || isFilesystemTool(tc)).length;
    }
    return toolCalls.length;
  }, [toolCalls, isV2Agent, isMcpTool, isFilesystemTool]);

  // Calculate the filtered index for display purposes (0-based)
  // This converts the absolute activeToolIndex to its position in the filtered MCP tools list
  const displayIndex = React.useMemo(() => {
    if (!isV2Agent) {
      return activeToolIndex; // V1: use absolute index as-is
    }

    // V2: Count how many MCP tools come before activeToolIndex
    let mcpToolsBeforeActive = 0;
    for (let i = 0; i < activeToolIndex && i < toolCalls.length; i++) {
      if (isMcpTool(toolCalls[i])) {
        mcpToolsBeforeActive++;
      }
    }
    // Return 0-based position in the filtered list
    return mcpToolsBeforeActive;
  }, [activeToolIndex, toolCalls, isV2Agent, isMcpTool]);

  // Determine if we should render the accordion
  const hasToolCalls = toolCalls && toolCalls.length > 0;
  // Only show accordion if there are tool calls or thinking or thinking summary
  // Don't show for simple responses with just content (those render in the main message body)
  const shouldShowAccordion = hasToolCalls || thinking || thinkingSummary;

  // Don't render if there's nothing to show
  if (!shouldShowAccordion) {
    return null;
  }

  const handleSheetOpen = (index: number) => {
    setActiveToolIndex(index);
    setIsSheetOpen(true);
  };

  const moveToNextTool = () => {
    // For V2 messages, only navigate to MCP and Filesystem tools
    if (isV2Agent) {
      let nextIndex = activeToolIndex + 1;
      while (nextIndex < toolCalls.length) {
        if (isMcpTool(toolCalls[nextIndex]) || isFilesystemTool(toolCalls[nextIndex])) {
          setActiveToolIndex(nextIndex);
          return;
        }
        nextIndex++;
      }
      // No more navigable tools found
    } else {
      // V1: navigate to any tool
      if (activeToolIndex < toolCalls.length - 1) {
        setActiveToolIndex(activeToolIndex + 1);
      }
    }
  };

  const moveToPrevTool = () => {
    // For V2 messages, only navigate to MCP and Filesystem tools
    if (isV2Agent) {
      let prevIndex = activeToolIndex - 1;
      while (prevIndex >= 0) {
        if (isMcpTool(toolCalls[prevIndex]) || isFilesystemTool(toolCalls[prevIndex])) {
          setActiveToolIndex(prevIndex);
          return;
        }
        prevIndex--;
      }
      // No more navigable tools found
    } else {
      // V1: navigate to any tool
      if (activeToolIndex > 0) {
        setActiveToolIndex(activeToolIndex - 1);
      }
    }
  };

  // Get the active tool
  const activeTool = toolCalls[activeToolIndex];

  // Toggle expanded state
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const directRenderTypes = ["UpdateTodoTool"];

  // Find the first tool in the concurrent batch
  // A tool is part of the batch if it's either:
  // 1. Not completed yet (still loading - no result or hasData is null)
  // 2. Has a pendingToolUse entry (needs approval or is approved)
  const firstBatchToolIndex = toolCalls.findIndex((toolCall: any) => {
    const hasCompletedResult = toolCall.toolResult && toolCall.toolResult.hasData !== null;
    const hasPendingToolUse = pendingToolUses.find(
      (toolUse) => toolUse.toolUseId === toolCall.toolUse?.id
    );

    return !hasCompletedResult || hasPendingToolUse !== undefined;
  });

  // Categorize tools in the batch
  const batchTools = firstBatchToolIndex !== -1 ? toolCalls.slice(firstBatchToolIndex) : [];
  const pendingApprovalTools = batchTools.filter((toolCall: any) => {
    const pendingToolUse = pendingToolUses.find(
      (toolUse) => toolUse.toolUseId === toolCall.toolUse?.id
    );
    return pendingToolUse?.status === "PENDING";
  });

  // Only show approval UI container when there are tools awaiting user approval
  const hasToolsAwaitingApproval = pendingApprovalTools.length > 0;
  const approvedTools = batchTools.filter((toolCall: any) => {
    const pendingToolUse = pendingToolUses.find(
      (toolUse) => toolUse.toolUseId === toolCall.toolUse?.id
    );
    return pendingToolUse?.status === "APPROVED";
  });
  const waitingTools = batchTools.filter((toolCall: any) => {
    const pendingToolUse = pendingToolUses.find(
      (toolUse) => toolUse.toolUseId === toolCall.toolUse?.id
    );
    return !pendingToolUse;
  });

  // Completed/approved tools (before the first batch tool)
  const completedToolCalls = hasToolsAwaitingApproval ? toolCalls.slice(0, firstBatchToolIndex) : toolCalls;

  const renderToolCall = (toolCall: any, index: number, isPending: boolean, isWaiting: boolean = false) => {
    const handleApprove = async () => {
      try {
        await reviewToolUse(
          toolCall.toolUse?.id || "",
          "APPROVED",
          undefined,
          llmConversationId,
          activeInteractionId
        );
      } catch (error) {
        console.error("Failed to approve tool use:", error);
      }
    };

    const handleReject = async () => {
      try {
        await reviewToolUse(
          toolCall.toolUse?.id || "",
          "REJECTED",
          undefined,
          llmConversationId,
          activeInteractionId
        );
      } catch (error) {
        console.error("Failed to reject tool use:", error);
      }
    };

    return (
      <div key={index} className="flex flex-col space-y-2">
        {toolCall.text && (
          <Markdown>{toolCall.text}</Markdown>
        )}
        <div>
          {toolCall.toolUse?.name &&
          directRenderTypes.includes(toolCall.toolUse.name) ? (
            <div>
              <div className="text-sm text-gray-400 font-mono">
                <ChecklistToolCallResult toolCall={toolCall} />
              </div>
              {toolCall.toolResult?.artifactId && (
                <CardBox title="Data">
                  <ArtifactCard
                    artifactId={toolCall.toolResult.artifactId}
                    groupTags={toolCall.toolResult?.groupTags || []}
                    title="Tool Result Data"
                    description="Data generated from the tool execution"
                    query=""
                  />
                </CardBox>
              )}
            </div>
          ) : (
            <ToolCallButton
              toolCall={toolCall}
              onClick={() => handleSheetOpen(index)}
              isPending={isPending}
              isWaiting={isWaiting}
              onApprove={isPending ? handleApprove : undefined}
              onReject={isPending ? handleReject : undefined}
              approvalInFlight={isInFlight}
            />
          )}
        </div>
      </div>
    );
  };

  const contentElement = (
    <div className={cn("flex flex-col p-2 whitespace-pre-wrap", isV2Agent ? "gap-0" : "gap-3")}>
      {/* Completed tools - use v2 rendering for v2 agents */}
      {isV2Agent ? (
        <V2ToolCallsList
          key={"V2ToolCallsList"}
          toolCalls={completedToolCalls}
          messageContent={messageContent}
          messageType={messageType}
          onToolClick={handleSheetOpen}
          isThinking={isThinking}
          thinking={thinking}
          thinkingSummary={thinkingSummary}
        />
      ) : (
        <>
          {/* Thinking section for non-v2 agents */}
          {thinking && (
            <div className="flex flex-col">
              <div
                ref={thinkingRef}
                className="text-sm text-muted-foreground mb-2 font-mono"
              >
                {thinking}
              </div>
            </div>
          )}
          {completedToolCalls.map((toolCall: any, index: number) =>
            renderToolCall(toolCall, index, false, false)
          )}
        </>
      )}

      {/* Pending batch section */}
      {hasToolsAwaitingApproval && (
        <div className="flex flex-col gap-3">
          {/* Render text content for all pending tools first */}
          {batchTools.map((toolCall: any, index: number) => (
            <div key={`text-${firstBatchToolIndex + index}`}>
              {toolCall.text && <Markdown>{toolCall.text}</Markdown>}
            </div>
          ))}

          {/* Unified batch container with border */}
          <div className="flex flex-col gap-3 rounded-lg border border-border bg-popover p-3">
            {/* Info banner */}
            {batchTools.length > 1 && (
              <div className="flex items-center gap-2 rounded-lg border border-blue-800/40 bg-blue-950/20 px-3 py-2 text-sm text-primary">
                <Info className="h-4 w-4 flex-shrink-0" />
                <span>
                  {pendingApprovalTools.length} of {batchTools.length} pending tool call{batchTools.length > 1 ? "s" : ""} require{pendingApprovalTools.length === 1 ? "s" : ""} approval
                </span>
              </div>
            )}

            {/* Pending approval tools (tool buttons only, text already rendered) */}
            {pendingApprovalTools.map((toolCall: any) => {
              const toolUseId = toolCall.toolUse?.id || "";
              const isApprovingThis = approvingToolId === toolUseId;
              const toolIndex = batchTools.findIndex((t: any) => t.toolUse?.id === toolCall.toolUse?.id);

              const handleApprove = async () => {
                try {
                  setApprovingToolId(toolUseId);
                  await reviewToolUse(
                    toolUseId,
                    "APPROVED",
                    undefined,
                    llmConversationId,
                    activeInteractionId
                  );
                  // Spinner will be cleared by useEffect when tool disappears from pendingToolUses
                } catch (error) {
                  console.error("Failed to approve tool use:", error);
                  setApprovingToolId(null);
                }
              };

              const handleReject = async () => {
                try {
                  setApprovingToolId(toolUseId);
                  await reviewToolUse(
                    toolUseId,
                    "REJECTED",
                    undefined,
                    llmConversationId,
                    activeInteractionId
                  );
                  // Spinner will be cleared by useEffect when tool disappears from pendingToolUses
                } catch (error) {
                  console.error("Failed to reject tool use:", error);
                  setApprovingToolId(null);
                }
              };

              // Guard against toolIndex === -1
              if (toolIndex === -1) {
                console.error("Tool not found in batchTools:", { toolUseId, batchTools });
                return (
                  <div key={toolUseId} className="text-red-400 text-sm px-2 py-1 bg-red-900/20 rounded border border-red-800">
                    ⚠️ Tool configuration error. Please refresh the page.
                  </div>
                );
              }

              return (
                <div key={toolUseId}>
                  <ToolCallButton
                    toolCall={toolCall}
                    onClick={() => handleSheetOpen(firstBatchToolIndex + toolIndex)}
                    isPending={true}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    approvalInFlight={isInFlight || isApprovingThis}
                  />
                </div>
              );
            })}

            {/* Separator if there are approved or waiting tools */}
            {(approvedTools.length > 0 || waitingTools.length > 0) && (
              <div className="border-t border-border my-1" />
            )}

            {/* Approved tools (waiting to execute, shown greyed out) */}
            {approvedTools.map((toolCall: any) => {
              const toolIndex = batchTools.findIndex((t: any) => t.toolUse?.id === toolCall.toolUse?.id);
              // Guard against toolIndex === -1
              if (toolIndex === -1) {
                console.error("Tool not found in batchTools:", { toolUseId: toolCall.toolUse?.id, batchTools });
                return (
                  <div key={toolCall.toolUse?.id} className="text-red-400 text-sm px-2 py-1 bg-red-900/20 rounded border border-red-800">
                    ⚠️ Tool configuration error. Please refresh the page.
                  </div>
                );
              }
              return (
                <div key={toolCall.toolUse?.id}>
                  <ToolCallButton
                    toolCall={toolCall}
                    onClick={() => handleSheetOpen(firstBatchToolIndex + toolIndex)}
                    isWaiting={true}
                  />
                </div>
              );
            })}

            {/* Waiting tools (no approval needed, greyed out, tool buttons only) */}
            {waitingTools.map((toolCall: any) => {
              const toolIndex = batchTools.findIndex((t: any) => t.toolUse?.id === toolCall.toolUse?.id);
              // Guard against toolIndex === -1
              if (toolIndex === -1) {
                console.error("Tool not found in batchTools:", { toolUseId: toolCall.toolUse?.id, batchTools });
                return (
                  <div key={toolCall.toolUse?.id} className="text-red-400 text-sm px-2 py-1 bg-red-900/20 rounded border border-red-800">
                    ⚠️ Tool configuration error. Please refresh the page.
                  </div>
                );
              }
              return (
                <div key={toolCall.toolUse?.id}>
                  <ToolCallButton
                    toolCall={toolCall}
                    onClick={() => handleSheetOpen(firstBatchToolIndex + toolIndex)}
                    isWaiting={true}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  if (disableAccordion) {
    return (
      <>
        <div className="flex flex-col mb-2 relative">{contentElement}</div>

        {/* Single shared sheet for all tool calls */}
        {isSheetOpen && activeTool && (
          <ToolCallSheet
            toolCall={activeTool}
            index={displayIndex}
            totalCount={totalNavigableTools}
            isOpen={isSheetOpen}
            onOpenChange={setIsSheetOpen}
            onNext={moveToNextTool}
            onPrevious={moveToPrevTool}
          />
        )}
      </>
    );
  }

  return (
    <div className={cn(hasToolCalls ? "mb-2" : "")}>
      <div className="flex flex-col relative">
        {/* Clickable header row with box around it */}
        <div
          onClick={toggleExpanded}
          className="flex justify-between items-center px-3 py-2 border border-border rounded-md bg-popover cursor-pointer hover:bg-secondary transition-colors"
        >
          <div className="flex items-center">
            {(thinking || thinkingSummary || toolCalls.length > 0) && (
              <div className="text-xs text-muted-foreground mr-2">
                {isThinking ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </div>
            )}
            <div className="text-xs text-muted-foreground">Agent Thought Process</div>
          </div>
        </div>

        {/* Collapsible content */}
        {isExpanded && (
          <div className="flex flex-col gap-3 border-l border-r border-b rounded-b border-border bg-popover">
            {contentElement}
          </div>
        )}
      </div>

      {/* Single shared sheet for all tool calls */}
      {isSheetOpen && activeTool && (
        <ToolCallSheet
          toolCall={activeTool}
          index={displayIndex}
          totalCount={totalNavigableTools}
          isOpen={isSheetOpen}
          onOpenChange={setIsSheetOpen}
          onNext={moveToNextTool}
          onPrevious={moveToPrevTool}
        />
      )}
    </div>
  );
};
